import Phaser from "phaser";
import { installLetterboxFit } from "../ui/responsive";
import type { BattleEvent, BattleReport, BattleResult } from "../../../../shared/types/BattleEvent";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";
import { loadBattleHistory, loadSettings } from "../../ai/localMemory";
import type { Language } from "../../i18n";
import mapConfig from "../data/mapConfig.json";

type ReplayEvent = BattleEvent & {
  label: string;
};

const IMPORTANT_EVENTS = new Set<BattleEvent["type"]>([
  "UNIT_SUMMONED",
  "ENEMY_KILLED",
  "PLAYER_UNIT_KILLED",
  "LANE_BREACHED",
  "CASTLE_DAMAGED",
  "COUNTER_ATTACK_STARTED",
  "ULTIMATE_USED",
  "UNIT_MOVE_COMMAND",
  "UNIT_LANE_CHANGED",
  "AI_MANEUVER_COMMAND",
  "AI_EVENT_TRIGGERED",
  "MERCHANT_OFFER_ACCEPTED",
  "MERCHANT_OFFER_EXPIRED",
  "AI_MERCY_GRANTED",
  "CUTIN_TRIGGERED",
  "OBJECTIVE_COMPLETED",
  "ECONOMY_OUTPOST_CAPTURED"
]);

export class ReplayScene extends Phaser.Scene {
  private language: Language = "ko";
  private result?: BattleResult;
  private replayEvents: ReplayEvent[] = [];
  private index = 0;
  private detailContainer?: Phaser.GameObjects.Container;
  private mapMarker?: Phaser.GameObjects.Graphics;
  private autoPlay = false;
  private nextAutoAt = 0;

  constructor() {
    super("ReplayScene");
  }

  create(data: { battleResult?: BattleResult; report?: BattleReport }) {
    installLetterboxFit(this);
    this.language = loadSettings().language;
    this.result = data.battleResult ?? loadBattleHistory().at(-1)?.result;
    this.add.image(640, 360, mapConfig.mapAssetKey).setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x08070a, 0.58);

    if (!this.result) {
      this.showEmptyState();
      return;
    }

    this.replayEvents = buildReplayEvents(this.result, this.language);
    this.index = 0;
    this.drawShell();
    this.renderCurrentEvent();
    this.input.keyboard?.on("keydown-LEFT", () => this.step(-1));
    this.input.keyboard?.on("keydown-RIGHT", () => this.step(1));
    this.input.keyboard?.on("keydown-SPACE", () => this.toggleAutoPlay());
    this.input.keyboard?.on("keydown-ESC", () => this.scene.start("ResultScene", { result: this.result?.result, report: data.report, battleResult: this.result }));
  }

  update(time: number) {
    if (!this.autoPlay || time < this.nextAutoAt) return;
    if (this.index >= this.replayEvents.length - 1) {
      this.autoPlay = false;
      return;
    }
    this.step(1);
    this.nextAutoAt = time + 1100;
  }

  private showEmptyState() {
    this.add
      .text(640, 300, this.language === "ko" ? "저장된 전투 기록이 없습니다." : "No battle replay is available.", titleStyle("#f7f2df", 24))
      .setOrigin(0.5);
    this.createButton(640, 386, this.language === "ko" ? "메뉴로" : "Menu", () => this.scene.start("MenuScene"));
  }

  private drawShell() {
    this.add.rectangle(640, 360, 1060, 610, 0x11131b, 0.9).setStrokeStyle(2, 0xc9b878, 0.9);
    this.add.text(158, 72, this.language === "ko" ? "전투 기록" : "Battle Replay", titleStyle("#ffe58e", 28));
    this.add.text(158, 108, this.summaryLine(), titleStyle("#d9e9ff", 14));
    this.add.rectangle(420, 342, 520, 390, 0x08070a, 0.42).setStrokeStyle(1, 0x5db7d0, 0.32);
    this.add.rectangle(874, 342, 356, 390, 0x08070a, 0.5).setStrokeStyle(1, 0xc9b878, 0.32);
    this.add.text(708, 160, this.language === "ko" ? "타임라인" : "Timeline", titleStyle("#c9b878", 16));
    this.createButton(390, 644, this.language === "ko" ? "이전" : "Prev", () => this.step(-1));
    this.createButton(540, 644, this.language === "ko" ? "다음" : "Next", () => this.step(1));
    this.createButton(710, 644, this.language === "ko" ? "자동 재생" : "Auto", () => this.toggleAutoPlay());
    this.createButton(900, 644, this.language === "ko" ? "결과로" : "Results", () => this.scene.start("ResultScene", { result: this.result?.result, battleResult: this.result }));
  }

  private renderCurrentEvent() {
    this.detailContainer?.destroy(true);
    this.mapMarker?.destroy();
    const current = this.replayEvents[this.index];
    if (!current) return;
    this.detailContainer = this.add.container(0, 0).setDepth(190);
    this.drawTimelineList(current);
    this.drawEventDetail(current);
    this.drawTimelineBar();
    this.drawMapMarker(current);
  }

  private drawTimelineList(current: ReplayEvent) {
    const start = Math.max(0, Math.min(this.index - 4, Math.max(0, this.replayEvents.length - 8)));
    this.replayEvents.slice(start, start + 8).forEach((event, offset) => {
      const y = 196 + offset * 42;
      const active = event.id === current.id;
      const row = this.add.rectangle(874, y, 318, 34, active ? 0x17364a : 0x11131b, active ? 0.94 : 0.62).setStrokeStyle(1, active ? 0x8de7ff : 0x2a3f4d, active ? 0.9 : 0.4);
      const text = this.add.text(728, y - 10, `${timeLabel(event.time)}  ${event.label}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "12px",
        color: active ? "#f7f2df" : "#c8d7ea",
        stroke: "#08070a",
        strokeThickness: 3,
        fixedWidth: 292
      });
      this.detailContainer?.add([row, text]);
    });
  }

  private drawEventDetail(event: ReplayEvent) {
    const laneText = event.lane ? (this.language === "ko" ? `${event.lane}번 전선` : `Lane ${event.lane}`) : this.language === "ko" ? "전체 전장" : "Whole field";
    const title = this.add.text(172, 164, `${timeLabel(event.time)} · ${laneText}`, titleStyle("#9be8ff", 18));
    const body = this.add.text(172, 206, eventDetail(event, this.language), {
      fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
      fontSize: "16px",
      color: "#f6f0df",
      stroke: "#08070a",
      strokeThickness: 4,
      wordWrap: { width: 468 }
    });
    const raw = this.add.text(172, 328, compactPayload(event), {
      fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
      fontSize: "12px",
      color: "#b8c4d8",
      stroke: "#08070a",
      strokeThickness: 3,
      wordWrap: { width: 468 }
    });
    this.detailContainer?.add([title, body, raw]);
  }

  private drawTimelineBar() {
    if (!this.result) return;
    const x = 172;
    const y = 574;
    const width = 936;
    const current = this.replayEvents[this.index];
    const ratio = current ? Phaser.Math.Clamp(current.time / Math.max(1, this.result.battleTime), 0, 1) : 0;
    const rail = this.add.rectangle(x + width / 2, y, width, 8, 0x2a3542, 0.9);
    const fill = this.add.rectangle(x + (width * ratio) / 2, y, width * ratio, 8, 0x8de7ff, 0.95).setOrigin(0.5);
    const dot = this.add.circle(x + width * ratio, y, 9, 0xffe58e, 0.95).setStrokeStyle(2, 0x08070a, 1);
    const label = this.add.text(x, y + 18, `${this.index + 1}/${Math.max(1, this.replayEvents.length)}`, titleStyle("#d9e9ff", 13));
    this.detailContainer?.add([rail, fill, dot, label]);
  }

  private drawMapMarker(event: ReplayEvent) {
    const point = eventPoint(event);
    this.mapMarker = this.add.graphics().setDepth(120);
    this.mapMarker.lineStyle(4, eventColor(event.type), 0.9);
    this.mapMarker.strokeCircle(point.x, point.y, 26);
    this.mapMarker.fillStyle(eventColor(event.type), 0.2);
    this.mapMarker.fillCircle(point.x, point.y, 22);
  }

  private step(delta: number) {
    if (this.replayEvents.length === 0) return;
    this.index = Phaser.Math.Clamp(this.index + delta, 0, this.replayEvents.length - 1);
    this.renderCurrentEvent();
  }

  private toggleAutoPlay() {
    this.autoPlay = !this.autoPlay;
    this.nextAutoAt = this.time.now + 500;
  }

  private createButton(x: number, y: number, label: string, onClick: () => void) {
    const button = this.add.container(x, y).setDepth(200);
    const frame = this.add.rectangle(0, 0, 132, 38, 0x17364a, 0.94).setStrokeStyle(1, 0xc9b878, 0.9);
    const text = this.add.text(0, 0, label, titleStyle("#f7f2df", 14)).setOrigin(0.5);
    button.add([frame, text]);
    button.setSize(132, 38);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => frame.setFillStyle(0x235372, 1));
    button.on("pointerout", () => frame.setFillStyle(0x17364a, 0.94));
    button.on("pointerdown", onClick);
  }

  private summaryLine() {
    if (!this.result) return "";
    const outcome = this.result.result === "win" ? (this.language === "ko" ? "승리" : "Win") : this.language === "ko" ? "패배" : "Loss";
    return this.language === "ko"
      ? `${outcome} · ${timeLabel(this.result.battleTime)} · 기록 ${this.replayEvents.length}개`
      : `${outcome} · ${timeLabel(this.result.battleTime)} · ${this.replayEvents.length} events`;
  }
}

function buildReplayEvents(result: BattleResult, language: Language): ReplayEvent[] {
  const important = result.events
    .filter((event) => IMPORTANT_EVENTS.has(event.type))
    .map((event) => ({ ...event, label: eventLabel(event, language) }));
  const source = important.length > 0 ? important : result.events.slice(-60).map((event) => ({ ...event, label: eventLabel(event, language) }));
  return source.sort((a, b) => a.time - b.time).slice(-100);
}

function titleStyle(color: string, fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
    fontSize: `${fontSize}px`,
    color,
    stroke: "#08070a",
    strokeThickness: 4
  };
}

function timeLabel(seconds: number) {
  const minute = Math.floor(seconds / 60);
  const second = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${minute}:${second}`;
}

function eventLabel(event: BattleEvent, language: Language) {
  const lane = event.lane ? (language === "ko" ? `${event.lane}번` : `L${event.lane}`) : "";
  const ko: Partial<Record<BattleEvent["type"], string>> = {
    UNIT_SUMMONED: `${lane} 소환`,
    ENEMY_KILLED: `${lane} 적 처치`,
    PLAYER_UNIT_KILLED: `${lane} 아군 손실`,
    LANE_BREACHED: `${lane} 돌파`,
    CASTLE_DAMAGED: `${lane} 성 피해`,
    COUNTER_ATTACK_STARTED: "반격 시작",
    ULTIMATE_USED: "궁극기",
    UNIT_MOVE_COMMAND: `${lane} 이동 명령`,
    UNIT_LANE_CHANGED: `${lane} 전선 변경`,
    AI_MANEUVER_COMMAND: `${lane} AI 기동`,
    AI_EVENT_TRIGGERED: "AI 이벤트",
    MERCHANT_OFFER_ACCEPTED: "상인 거래",
    MERCHANT_OFFER_EXPIRED: "상인 만료",
    AI_MERCY_GRANTED: "자비",
    CUTIN_TRIGGERED: "컷인",
    OBJECTIVE_COMPLETED: `${lane} 목표 완료`,
    ECONOMY_OUTPOST_CAPTURED: `${lane} 거점 점령`
  };
  const en: Partial<Record<BattleEvent["type"], string>> = {
    UNIT_SUMMONED: `${lane} summon`,
    ENEMY_KILLED: `${lane} enemy down`,
    PLAYER_UNIT_KILLED: `${lane} unit lost`,
    LANE_BREACHED: `${lane} breach`,
    CASTLE_DAMAGED: `${lane} castle hit`,
    COUNTER_ATTACK_STARTED: "counterattack",
    ULTIMATE_USED: "ultimate",
    UNIT_MOVE_COMMAND: `${lane} order`,
    UNIT_LANE_CHANGED: `${lane} lane change`,
    AI_MANEUVER_COMMAND: `${lane} AI maneuver`,
    AI_EVENT_TRIGGERED: "AI event",
    MERCHANT_OFFER_ACCEPTED: "merchant deal",
    MERCHANT_OFFER_EXPIRED: "merchant expired",
    AI_MERCY_GRANTED: "mercy",
    CUTIN_TRIGGERED: "cut-in",
    OBJECTIVE_COMPLETED: `${lane} objective`,
    ECONOMY_OUTPOST_CAPTURED: `${lane} outpost`
  };
  return (language === "ko" ? ko[event.type] : en[event.type]) ?? event.type;
}

function eventDetail(event: BattleEvent, language: Language) {
  if (language === "ko") {
    if (event.type === "CASTLE_DAMAGED") return `${event.payload.owner === "player" ? "아군 성" : "적 요새"}이 ${Number(event.payload.damage ?? 0)} 피해를 받았습니다.`;
    if (event.type === "UNIT_SUMMONED") return `${String(event.payload.unitId ?? "유닛")}이 전선에 투입됐습니다.`;
    if (event.type === "AI_MANEUVER_COMMAND") return `AI가 ${String(event.payload.mode ?? "기동")} 명령으로 전선을 재배치했습니다.`;
    if (event.type === "AI_EVENT_TRIGGERED") return `AI 이벤트가 발동했습니다: ${String(event.payload.type ?? "unknown")}`;
    if (event.type === "ULTIMATE_USED") return `${String(event.payload.faction ?? "unknown")} 궁극기 ${String(event.payload.type ?? "")} 발동.`;
    return eventLabel(event, language);
  }
  if (event.type === "CASTLE_DAMAGED") return `${event.payload.owner === "player" ? "Your castle" : "Enemy fortress"} took ${Number(event.payload.damage ?? 0)} damage.`;
  if (event.type === "UNIT_SUMMONED") return `${String(event.payload.unitId ?? "Unit")} entered the lane.`;
  if (event.type === "AI_MANEUVER_COMMAND") return `AI repositioned forces with ${String(event.payload.mode ?? "maneuver")}.`;
  if (event.type === "AI_EVENT_TRIGGERED") return `AI event triggered: ${String(event.payload.type ?? "unknown")}.`;
  if (event.type === "ULTIMATE_USED") return `${String(event.payload.faction ?? "unknown")} ultimate ${String(event.payload.type ?? "")} triggered.`;
  return eventLabel(event, language);
}

function compactPayload(event: BattleEvent) {
  const payload = JSON.stringify(event.payload);
  return payload.length > 180 ? `${payload.slice(0, 177)}...` : payload;
}

function eventPoint(event: BattleEvent) {
  const laneY = laneToY(event.lane);
  const payloadX = Number(event.payload.targetX ?? event.payload.x);
  if (Number.isFinite(payloadX)) return { x: Phaser.Math.Clamp(payloadX, 120, 1160), y: laneY };
  if (event.type === "CASTLE_DAMAGED") {
    return { x: event.payload.owner === "player" ? mapConfig.playerCastleX : mapConfig.aiFortressX, y: laneY };
  }
  if (event.type === "AI_EVENT_TRIGGERED" || event.type === "ULTIMATE_USED") return { x: 640, y: laneY };
  return { x: 640, y: laneY };
}

function laneToY(lane: LaneNumber | undefined) {
  if (!lane) return 360;
  return mapConfig.lanes.find((item) => item.lane === lane)?.y ?? 360;
}

function eventColor(type: BattleEvent["type"]) {
  if (type === "CASTLE_DAMAGED" || type === "LANE_BREACHED") return 0xff8c73;
  if (type === "ENEMY_KILLED" || type === "OBJECTIVE_COMPLETED") return 0x9be8ff;
  if (type === "ULTIMATE_USED" || type === "CUTIN_TRIGGERED") return 0xffe58e;
  if (type === "AI_EVENT_TRIGGERED" || type === "AI_MANEUVER_COMMAND") return 0xffb15a;
  return 0xd9e9ff;
}
